import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import { dayMs } from "verdi-common/timeConstants";
import type { AxisSpec, CardRelation, StreamEntry } from "../ChartSpec";
import { BUCKET_WIDTH_MS, type Resolution } from "../resolution/pick-resolution";
import type { StreamPoint } from "../stream-cache/stream-cache-store";
import type { WindowedStreams } from "../stream-data/types";

/** Converts a raw database value to the display unit. Receives a unitCategory string (from the wire). */
export type GetConversion = (unitCategory: string) => (value: number) => number;

export type PreparedSeries = {
    streamId: number;
    color: string;
    name: string;
    /** Which sensor cluster on the device. The legend row shows it beside the metric. */
    sensorClusterName: string | null;
    /** Which channel on that cluster. The legend shows it when there is no cluster name and it isn't just the metric again. */
    sensorElementName: string | null;
    /** The zones the stream sits in. The legend heads its rows with them. */
    zoneNames: string[];
    /** How the stream relates to the card the viewer sits on. Absent reads as the card's own. */
    cardRelation?: CardRelation;
    /** The axis this series is bound to — chart options and hover marks both address it by id. */
    axisId: string;
    /** What this series measures. The legend row names it. */
    unitCategory: string;
    /** The category the axis is scaled in, which is the unit these values were converted to. */
    axisUnitCategory: string;
    seriesType: "line" | "bar";
    /** The interval each point covers, from the bucket width it was fetched at. 0 for raw points. */
    bucketMs: number;
    /** Each reading at its own time, so a bar's is its bucket's start. The chart shifts bars when it draws them. */
    points: [Date, number | null][];
};

/**
 * Echarts sizes every bar from the smallest gap between points in the series, so empty buckets stay
 * in the data — drop them and a gauge that reports sparsely gets bars wider than the bucket they
 * sum. A null draws no bar, so nothing marks the empty bucket.
 */
function barPoints(points: StreamPoint[], convert: (value: number) => number): [Date, number | null][] {
    return points.map((point) => [new Date(point.t), point.avg === null ? null : convert(point.avg)]);
}

/** Bars never reach the raw tier in the app, but debugForceRawStreams can get there, and a raw point spans no interval. */
function bucketMsFor(resolution: Resolution): number {
    return resolution === "raw" ? 0 : BUCKET_WIDTH_MS[resolution];
}

/** How long a reading stays current: the line breaks after it, the cursor readout drops it, and a live legend row goes muted. */
export function staleAfterMsFor(unitCategory: string): number {
    return getUnitCategoryConfiguration(unitCategory)?.staleAfterMs ?? dayMs;
}

function linePoints(
    points: StreamPoint[],
    convert: (value: number) => number,
    staleAfterMs: number,
): [Date, number | null][] {
    const nullInsertedPoints: [Date, number | null][] = [];

    points
        // Empty buckets arrive as nulls; drop them so the time delta below is calculated between real readings
        .filter((point): point is StreamPoint & { avg: number } => point.avg !== null)
        .forEach((point, index, array) => {
            nullInsertedPoints.push([new Date(point.t), convert(point.avg)]);

            const nextPoint = array[index + 1];

            // With ECharts connectNulls: false, inserting our own null value breaks the line when
            // we've detected a stale period
            if (nextPoint && nextPoint.t - point.t > staleAfterMs) {
                nullInsertedPoints.push([new Date(point.t + staleAfterMs), null]);
            }
        });

    return nullInsertedPoints;
}

/**
 * Joins the spec with the stream data to make one series per spec stream. A stream the cache has
 * nothing for yet still gets a series, with no points, so the legend lists it while it loads.
 */
export function prepareSeries(
    dataByStreamId: WindowedStreams,
    streams: StreamEntry[],
    axes: AxisSpec[],
    getConversion: GetConversion,
    /** A caller's own rule for when a line breaks, such as a sparkline's; the unit category's otherwise */
    { staleAfterMs: ownStaleAfterMs }: { staleAfterMs?: number } = {},
): PreparedSeries[] {
    return streams.map((stream) => {
        const data = dataByStreamId[stream.streamId];
        const axis = axes.find((a) => a.id === stream.axisId);

        // Axes are built from these streams, so a matching axis always exists.
        if (!axis) {
            throw new Error(`No axis found for stream ${stream.streamId} (axisId ${stream.axisId})`);
        }

        const convert = getConversion(axis.unitCategory);

        const staleAfterMs = ownStaleAfterMs ?? staleAfterMsFor(axis.unitCategory);

        let points: [Date, number | null][] = [];
        if (data) {
            points =
                stream.seriesType === "bar"
                    ? barPoints(data.points, convert)
                    : linePoints(data.points, convert, staleAfterMs);
        }

        return {
            color: stream.color,
            streamId: stream.streamId,
            name: stream.name,
            sensorClusterName: stream.sensorClusterName ?? null,
            sensorElementName: stream.sensorElementName ?? null,
            zoneNames: stream.zoneNames ?? [],
            ...(stream.cardRelation ? { cardRelation: stream.cardRelation } : {}),
            axisId: axis.id,
            unitCategory: stream.unitCategory,
            axisUnitCategory: axis.unitCategory,
            seriesType: stream.seriesType,
            bucketMs: data ? bucketMsFor(data.resolution) : 0,
            points,
        };
    });
}
